import React, { useState, useEffect } from 'react';
import axios from 'axios';

const StudentSpace = ({ setCurrentPage }) => {
  const [departments, setDepartments] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [classes, setClasses] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [filters, setFilters] = useState({
    department: '',
    academicYear: '',
    semester: 1,
    class: ''
  });

  const timeSlots = [
    '8h30-10h00', '10h10-11h40', '11h50-13h20',
    '13h30-15h00', '15h10-16h40', '16h50-18h20'
  ];

  const days = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (filters.department && filters.academicYear) {
      fetchClassesByDepartmentAndYear();
    }
  }, [filters.department, filters.academicYear]);

  useEffect(() => {
    if (filters.class) {
      fetchSessions();
    }
  }, [filters.class, filters.semester]);

  const fetchInitialData = async () => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || '';
      const [departmentsRes, academicYearsRes] = await Promise.all([
        axios.get(`${apiUrl}/api/departments`),
        axios.get(`${apiUrl}/api/academic-years`)
      ]);
      setDepartments(departmentsRes.data);
      setAcademicYears(academicYearsRes.data);
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
    }
  };

  const fetchClassesByDepartmentAndYear = async () => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || '';
      const response = await axios.get(`${apiUrl}/api/classes?department=${filters.department}&academicYear=${filters.academicYear}&limit=1000`);
      setClasses(response.data);
      setFilters(prev => ({ ...prev, class: '' }));
      setSessions([]);
    } catch (error) {
      console.error('Erreur lors du chargement des classes:', error);
    }
  };

  const fetchSessions = async () => {
    setLoading(true);
    try {
      const apiUrl = import.meta.env.VITE_API_URL || '';
      const response = await axios.get(`${apiUrl}/api/sessions/public?class=${filters.class}&semester=${filters.semester}&_t=${Date.now()}`);
      setSessions(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({
      ...filters,
      [name]: name === 'semester' ? Number(value) : value
    });
  };

  const getSessionsForSlot = (dayIndex, timeSlotIndex) => {
    return sessions.filter(session => 
      session.dayOfWeek === dayIndex + 1 && 
      session.timeSlot === timeSlotIndex + 1
    );
  };

  const getTeacherColor = (teacherId) => {
    const colors = [
      'bg-red-100 text-red-800', 'bg-blue-100 text-blue-800', 'bg-green-100 text-green-800',
      'bg-yellow-100 text-yellow-800', 'bg-purple-100 text-purple-800', 'bg-pink-100 text-pink-800',
      'bg-indigo-100 text-indigo-800', 'bg-gray-100 text-gray-800'
    ];
    const hash = teacherId?.split('').reduce((a, b) => {
      a = ((a << 5) - a) + b.charCodeAt(0);
      return a & a;
    }, 0) || 0;
    return colors[Math.abs(hash) % colors.length];
  };

  const selectedClass = classes.find(cls => cls._id === filters.class);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <button onClick={() => setCurrentPage('home')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
              ← Retour à l'accueil
            </button>
            
            {/* Desktop Menu */}
            <div className="hidden md:flex gap-2">
              <button onClick={() => setCurrentPage('admin')} className="px-3 py-1 text-sm font-medium text-primary-700 bg-primary-50 rounded hover:bg-primary-100">Admin</button>
              <button onClick={() => setCurrentPage('teacher')} className="px-3 py-1 text-sm font-medium text-green-700 bg-green-50 rounded hover:bg-green-100">Enseignant</button>
              <button onClick={() => setCurrentPage('contact')} className="px-3 py-1 text-sm font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200">Contact</button>
            </div>
            
            {/* Mobile Menu Button */}
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden p-2 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {mobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
          
          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden pb-4">
              <div className="flex flex-col space-y-2">
                <button onClick={() => { setCurrentPage('admin'); setMobileMenuOpen(false); }} className="w-full text-left px-3 py-2 text-sm font-medium text-primary-700 bg-primary-50 rounded hover:bg-primary-100">Admin</button>
                <button onClick={() => { setCurrentPage('teacher'); setMobileMenuOpen(false); }} className="w-full text-left px-3 py-2 text-sm font-medium text-green-700 bg-green-50 rounded hover:bg-green-100">Enseignant</button>
                <button onClick={() => { setCurrentPage('contact'); setMobileMenuOpen(false); }} className="w-full text-left px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200">Contact</button>
              </div>
            </div>
          )}
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Espace Étudiant</h1>
          <p className="text-gray-600">Consultez votre emploi du temps en sélectionnant votre classe</p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Département</label>
              <select 
                name="department" 
                value={filters.department} 
                onChange={handleFilterChange}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">Sélectionner un département</option>
                {departments.map(dept => (
                  <option key={dept._id} value={dept._id}>{dept.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Année universitaire</label>
              <select 
                name="academicYear" 
                value={filters.academicYear} 
                onChange={handleFilterChange}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">Sélectionner une année</option>
                {academicYears.map(year => (
                  <option key={year._id} value={year._id}>{year.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Semestre</label>
              <select 
                name="semester" 
                value={filters.semester} 
                onChange={handleFilterChange}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                {[1, 2].map(sem => (
                  <option key={sem} value={sem}>Semestre {sem}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Classe</label>
              <select 
                name="class" 
                value={filters.class} 
                onChange={handleFilterChange}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">Sélectionner une classe</option>
                {classes.map(cls => (
                  <option key={cls._id} value={cls._id}>{cls.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Loading */}
        {loading && (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-2"></div>
            <p className="text-sm text-gray-600">Chargement de l'emploi du temps...</p>
          </div>
        )}

        {/* Schedule */}
        {selectedClass && !loading && (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Emploi du temps - {selectedClass.name}</h2>
              <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                <span>Semestre {filters.semester}</span>
                <span>•</span>
                <span>{sessions.length} séances</span>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Horaires</th>
                    {days.map((day, index) => (
                      <th key={index} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{day}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {timeSlots.map((timeSlot, timeIndex) => (
                    <tr key={timeIndex}>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="text-xs">
                          <div className="font-medium text-gray-900">{timeIndex + 1}</div>
                          <div className="text-gray-500">{timeSlot}</div>
                        </div>
                      </td>
                      {days.map((day, dayIndex) => {
                        const sessionsForSlot = getSessionsForSlot(dayIndex, timeIndex);
                        return (
                          <td key={dayIndex} className="px-4 py-4">
                            {sessionsForSlot.map(session => (
                              <div 
                                key={session._id}
                                className={`p-2 rounded text-xs mb-1 ${getTeacherColor(session.teacher?._id)}`}
                              >
                                <div className="font-medium">{session.course?.code || session.course?.name}</div>
                                <div className="text-xs opacity-75">
                                  {session.teacher?.firstName} {session.teacher?.lastName}
                                </div>
                                <div className="text-xs opacity-75">{session.room?.name || 'Sans salle'}</div>
                                {session.group && (
                                  <div className="text-xs opacity-75">{session.group}</div>
                                )}
                              </div>
                            ))}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* No selection */}
        {!filters.class && !loading && (
          <div className="text-center py-16">
            <div className="text-6xl mb-4">📅</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Sélectionnez votre classe</h3>
            <p className="text-gray-600">Choisissez votre département, année universitaire et classe pour afficher votre emploi du temps</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentSpace;